import { RecentAnswer } from "winslow-core";
import "./CollectionStatsQuestions.css";
import { CollectionStatsQuestionList } from "./CollectionStatsQuestionList";
import CollectionStatsQuestionCards from "./CollectionStatsQuestionCards";
import { useEffect, useState } from "react";
import { getCategoryColor } from "./colorData";

type CollectionStatsQuestionsProps = {
  questions: RecentAnswer[];
  filterName?: string;
  filterCategory?: string;
  filterSource?: string;
  view: "cards" | "list";
};

export const CollectionStatsQuestions = ({
  questions,
  filterName,
  filterCategory,
  filterSource,
  view,
}: CollectionStatsQuestionsProps) => {
  const [visibleInList, setVisibleInList] = useState<RecentAnswer[]>(
    questions.length >= 40 ? questions.slice(0, 40) : questions
  );
  const [visibleInCards, setVisibleInCards] = useState<RecentAnswer[]>(
    questions.length >= 20 ? questions.slice(0, 20) : questions
  );

  const [sortedQuestions, setSortedQuestions] = useState<RecentAnswer[]>(
    questions.sort((a: RecentAnswer, b: RecentAnswer) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    })
  );

  const [loadMoreVisibleList, setLoadMoreVisibleList] = useState<boolean>(
    questions.length > 40
  );
  const [loadMoreVisibleCards, setLoadMoreVisibleCards] = useState<boolean>(
    questions.length > 20
  );

  const [categoryColors, setCategoryColors] = useState<
    { category: string; color: string }[]
  >([]);

  const loadMoreList = () => {
    const newVisibleCount = visibleInList.length + 40;
    setVisibleInList(sortedQuestions.slice(0, newVisibleCount));
    setLoadMoreVisibleList(newVisibleCount < sortedQuestions.length);
  };

  const loadMoreCards = () => {
    const newVisibleCount = visibleInCards.length + 20;
    setVisibleInCards(sortedQuestions.slice(0, newVisibleCount));
    setLoadMoreVisibleCards(newVisibleCount < sortedQuestions.length);
  };

  useEffect(() => {
    setVisibleInList(
      sortedQuestions.length >= 40
        ? sortedQuestions.slice(0, 40)
        : sortedQuestions
    );
    setVisibleInCards(
      sortedQuestions.length >= 20
        ? sortedQuestions.slice(0, 20)
        : sortedQuestions
    );
    setLoadMoreVisibleCards(sortedQuestions.length > 20);
    setLoadMoreVisibleList(sortedQuestions.length > 40);
  }, [sortedQuestions]);

  useEffect(() => {
    const newFilteredQuestions = [
      ...questions
        .filter((question: RecentAnswer) => {
          if (!filterName) {
            return true;
          }

          return question.name?.toLowerCase() === filterName?.toLowerCase();
        })
        .filter((question: RecentAnswer) => {
          if (!filterCategory) {
            return true;
          }

          return (
            question.category?.toLowerCase() === filterCategory?.toLowerCase()
          );
        })
        .filter((question: RecentAnswer) => {
          if (!filterSource) {
            return true;
          }

          return question.platformSource === filterSource;
        }),
    ];
    setSortedQuestions(newFilteredQuestions);
  }, [filterName, filterCategory, filterSource]);

  useEffect(() => {
    const categoryMap: Map<string, boolean> = new Map<string, boolean>();

    questions.forEach((response: RecentAnswer) => {
      categoryMap.set(response.category, true);
    });

    const categories = Array.from(categoryMap.keys());

    const categoryColorsResult = categories.map(
      (category: string, i: number) => {
        return {
          category,
          color: getCategoryColor(category),
        };
      }
    );
    if (categoryColorsResult) {
      setCategoryColors(categoryColorsResult);
    }
  }, []);

  return (
    <>
      {view === "list" && (
        <>
          <CollectionStatsQuestionList
            questions={visibleInList}
            categoryColors={categoryColors}
          />
          {loadMoreVisibleList && (
            <div className="load-more-container" onClick={loadMoreList}>
              <div className="load-more-text">View More</div>
              <img
                src="/icons/down-arrow.svg"
                alt=""
                className="load-more-image"
              />
            </div>
          )}
        </>
      )}
      {view === "cards" && (
        <>
          <CollectionStatsQuestionCards questions={visibleInCards} />
          {loadMoreVisibleCards && (
            <div className="load-more-container" onClick={loadMoreCards}>
              <div className="load-more-text">View More</div>
              <img
                src="/icons/down-arrow.svg"
                alt=""
                className="load-more-image"
              />
            </div>
          )}
        </>
      )}
    </>
  );
};
